import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { DcCoreElementType } from '../../core/dc-core-types';
import { DcBaseRequester } from './dc-base.requester';
import { IMessage } from './models/message.model';
import { MetadataTypeBE } from './models/metadata.model';
import { IProjectElements } from './models/project-elements.model';
import { IProjectMemberModel } from './models/project-member.model';
import {
	IProjectGdStatResponse,
	IProjectHdStatResponse,
} from './models/project-stat.model';
import { IProjectTraceResponse } from './models/project-trace.model';
import { IProjectModel } from './models/project.model';

@Injectable({
	providedIn: 'root',
})
export class ProjectRequester extends DcBaseRequester<IProjectModel> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;
	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('PROJECTS_GET_ALL');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getAllProjects(
		useCache = true
	): Observable<ReadonlyArray<IProjectModel>> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_GET_ALL'),
			{},
			{}
		);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  });

		return this.get<ReadonlyArray<IProjectModel>>(url, cachePolicy);
	}

	public getOrphanProjects(
		useCache = true
	): Observable<ReadonlyArray<IProjectModel>> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_ORPHAN'),
			{},
			{}
		);
		return this.get<Array<IProjectModel>>(
			url,
			CachePolicyEntity.build({
				policy: CachePolicy.NetworkAndCache,
				maxAgeInSeconds: 300,
				isCacheable: true,
			})
		);
	}

	public setOrphanProjectAdmins(
		id: number,
		admins: Array<string>
	): Observable<void> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_ADMIN_MEMBERS'),
			{
				id,
			},
			{}
		);
		return this.http.post<void>(url, admins);
	}

	public getProjects(
		useCache = true
	): Observable<ReadonlyArray<IProjectModel>> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_GET_ALL'),
			{},
			{}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IProjectModel>>(url, cachePolicy);
	}

	public setDefaultProject(
		id: number,
		isDefault: boolean
	): Observable<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_DEFAULT_UPDATE'),
			{
				id,
			},
			{
				project: id,
			}
		);
		return this.http.put<boolean>(url, isDefault);
	}

	public setFavoriteProject(
		id: number,
		isFavorite: boolean
	): Observable<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_FAVORITE'),
			{
				id,
			},
			{
				project: id,
			}
		);
		return this.http.put<boolean>(url, isFavorite);
	}

	public getProjectMembers(
		projectId: number,
		useCache = true
	): Observable<ReadonlyArray<IProjectMemberModel>> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_MEMBERS'),
			{
				id: projectId,
			},
			{
				project: projectId,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.CacheOnly,
					maxAgeInSeconds: 30,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IProjectMemberModel>>(url, cachePolicy);
	}

	public sendEmail(
		projectId: number,
		recipientId: string,
		message: { subject: string; body: string }
	): Observable<void> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_MESSAGE'),
			{
				id: projectId,
			},
			{
				project: projectId,
			}
		);

		const payload: IMessage = {
			// eslint-disable-next-line camelcase
			recipient_user_id: recipientId,
			subject: message.subject,
			// eslint-disable-next-line camelcase
			message_body: message.body,
		};
		return this.http.post<void>(url, payload);
	}

	public isProjectCritical(projectId: number): Observable<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_CRITICAL'),
			{
				id: projectId,
			},
			{
				project: projectId,
			}
		);
		return this.get<boolean>(url);
	}

	public isProjectUsed(projectId: number): Observable<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_USED_IN_OTHER_PROJECT'),
			{
				id: projectId,
			},
			{
				project: projectId,
			}
		);
		return this.get<boolean>(url);
	}

	public getProjectElements(
		projectId: number,
		useCache = true
	): Observable<IProjectElements> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_ELEMENTS'),
			{
				id: projectId,
			},
			{
				project: projectId,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<IProjectElements>(url, cachePolicy);
	}

	public sendVerificationCode(id: number): Observable<void> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_DELETE_VERIFICATION'),
			{
				id,
			},
			{
				project: id,
			}
		);
		return this.http.get<void>(url);
	}
	public deleteProject(id: number, token?: string): Observable<void> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PROJECTS_DELETE'),
			{
				id,
			},
			{
				project: id,
				token,
			}
		);
		return this.http.delete<void>(url);
	}

	public getProject(useCache = true): Observable<IProjectModel> {
		const resourceUrl = this.generateResourceUrl('PROJECTS_GET');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: this.projectId,
			},
			{
				project: this.projectId,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<IProjectModel>(url, cachePolicy);
	}

	public getProjectGenericsStat(
		useCache = true
	): Observable<IProjectGdStatResponse> {
		const resourceUrl = this.generateResourceUrl('PROJECTS_STAT_GD');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<IProjectGdStatResponse>(url, cachePolicy);
	}

	public getProjectHandleStat(
		useCache = true
	): Observable<IProjectHdStatResponse> {
		const resourceUrl = this.generateResourceUrl('PROJECTS_STAT_HD');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<IProjectHdStatResponse>(url, cachePolicy);
	}

	public getElementTraces(
		dcType: MetadataTypeBE,
		useCache = true
	): Observable<ReadonlyArray<IProjectTraceResponse>> {
		const resourceUrl = this.generateResourceUrl('PROJECTS_TRACES');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				dcType,
			},
			{
				project: this.projectId,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<ReadonlyArray<IProjectTraceResponse>>(url, cachePolicy);
	}

	public canCreateElement(elementType: DcCoreElementType): Observable<boolean> {
		const resourceUrl = this.generateResourceUrl(
			'PERMISSIONS_CHECK_USER_GLOBAL_PERMISSION'
		);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				action: this.getCreateAction(elementType),
			},
			{
				project: this.projectId,
			}
		);
		return this.get<boolean>(url);
	}

	private getCreateAction(type: DcCoreElementType): string {
		switch (type) {
			case DcCoreElementType.CONNECTOR:
				return 'connector-create';
			case DcCoreElementType.ENTITY:
				return 'entity-create';
			case DcCoreElementType.DATABLOCK:
				return 'datablock-create';
			case DcCoreElementType.DASHBOARD:
				return 'dashboard-create';
			case DcCoreElementType.DEPOT:
				return 'data_source-create';
			case DcCoreElementType.MAP:
				return 'map-create';
			case DcCoreElementType.CHART:
				return 'chart-create';
			case DcCoreElementType.TIMELINE:
				return 'timeline-create';
			case DcCoreElementType.GRID:
				return 'grid-create';
			case DcCoreElementType.MEDIA:
				return 'media-create';
			case DcCoreElementType.EXPOSITION:
				return 'exposition-create';
			default:
				return '';
		}
	}
}
